import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { FileText, AlertCircle, Download, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { getIRNReportVersions } from '../../../../../redux/ProductionData/actions';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';

interface IRNReportsViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number | string;
  projectName: string;
  jobNumber?: string;
  refresh?: () => void;
}

export const IRNReportsViewModal: React.FC<IRNReportsViewModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  jobNumber,
  refresh,
}) => {
  const dispatch = useDispatch();

  // Get IRN report versions state from Redux
  const {
    irnReportVersionsLoading,
    irnReportVersions,
    irnReportVersionsError
  } = useSelector((state: any) => state.productionData || {});

  // Versions expand/collapse state per pipe
  const [expandedPipes, setExpandedPipes] = useState<Set<string>>(new Set());

  // PDF Viewer state
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  // Fetch reports when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      dispatch(getIRNReportVersions(projectId));
    } else if (!isOpen) {
      // Reset state when modal closes
      setExpandedPipes(new Set());
      setViewerFile(null);
    }
  }, [isOpen, projectId, dispatch]);

  // Process versions - group by pipe with latest and older versions
  const processedVersions = useMemo(() => {
    if (!irnReportVersions || typeof irnReportVersions !== 'object') {
      return {};
    }
    
    const result: Record<string, { latest: any | null; older: any[]; total: number; pipeName?: string; pipeSize?: string }> = {};
    
    Object.keys(irnReportVersions).forEach((pipeId) => {
      const docs = irnReportVersions[pipeId] || [];
      if (docs.length === 0) return;
      
      // Sort by docRef descending (latest first)
      const sorted = [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });
      
      // Get pipe info from first document
      const firstDoc = sorted[0];
      result[pipeId] = {
        latest: sorted[0] || null,
        older: sorted.slice(1),
        total: sorted.length,
        pipeName: firstDoc?.pipeName || firstDoc?.details?.pipeName || `Pipe ${pipeId}`,
        pipeSize: firstDoc?.pipeSize || firstDoc?.details?.pipeSize || ''
      };
    });
    
    return result;
  }, [irnReportVersions]);

  // Get total document count across all pipes
  const totalDocumentsCount = useMemo(() => {
    return Object.values(processedVersions).reduce((sum, group) => sum + (group?.total || 0), 0);
  }, [processedVersions]);

  // Toggle pipe expansion
  const togglePipeExpansion = (pipeId: string) => {
    setExpandedPipes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pipeId)) {
        newSet.delete(pipeId);
      } else {
        newSet.add(pipeId);
      }
      return newSet;
    });
  };

  // Handle download/view
  const handleDownloadReport = (doc: any) => {
    if (!doc || !(doc.fileUrl || doc.file_url)) {
      return;
    }
    setViewerFile({
      fileUrl: doc.fileUrl || doc.file_url,
      fileName: doc.fileName || doc.file_name || 'IRN Report'
    });
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Compute header info items for display (project, job)
  const headerInfoItems = useMemo(() => {
    const infoItems = [];
    
    // Add project name
    if (projectName) {
      infoItems.push({ label: 'Project', value: projectName, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
    }
    
    // Add job number
    if (jobNumber) {
      infoItems.push({ label: 'Job', value: jobNumber, bgColor: 'bg-cyan-100 text-cyan-800' });
    }
    
    return infoItems;
  }, [projectName, jobNumber]);

  // Close PDF viewer
  const closeViewer = () => {
    setViewerFile(null);
  };

  const isLoading = irnReportVersionsLoading;
  const error = irnReportVersionsError;

  // If viewer is open, render only the viewer
  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={closeViewer}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[1100px] w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col bg-page-bg"
      >
        <DialogHeader className="flex-shrink-0 pl-4 sm:pl-6 pr-14 py-4 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between gap-4">
          <div className="min-w-0">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Inspection Release Note (IRN) Reports
            </DialogTitle>
            <p className="text-[12px] text-heritage-grey mt-1 truncate">
              {projectName && `Project: ${projectName}`}
              {jobNumber && <span className="ml-2">· Job: {jobNumber}</span>}
            </p>
          </div>
        </DialogHeader>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-heritage-grey mr-2" />
              <span className="text-[13px] text-heritage-grey">Loading previous reports...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
              <AlertCircle className="h-5 w-5 text-hero-red" />
              <span className="text-[13px] text-hero-red">{error}</span>
            </div>
          ) : Object.keys(processedVersions).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#E2E5EA] rounded-xl">
              <div className="w-12 h-12 rounded-xl bg-page-bg flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-heritage-grey" />
              </div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-1">No Reports Yet</h3>
              <p className="text-[12px] text-heritage-grey max-w-xs text-center">
                No IRN reports have been generated yet.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(processedVersions).map(([pipeId, { latest, older, total, pipeName, pipeSize }]) => {
                if (!latest) return null;
                const isExpanded = expandedPipes.has(pipeId);
                const hasOlderVersions = older.length > 0;
                const displayName = pipeSize ? `${pipeName} (${pipeSize})` : pipeName;

                return (
                  <div key={pipeId} className="bg-white border border-[#E2E5EA] rounded-xl overflow-hidden">
                    <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center justify-between">
                      <h4 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wider">
                        {displayName}
                      </h4>
                      <span className="text-[11px] text-heritage-grey">
                        {total} {total === 1 ? 'version' : 'versions'}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-page-bg transition-colors">
                      <div className="flex items-center gap-2 flex-shrink-0 min-w-[110px]">
                        {latest.docRef && (
                          <span className="text-[11px] px-2 py-0.5 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-full font-semibold">
                            v{latest.docRef}
                          </span>
                        )}
                        <span className="text-[11px] px-2 py-0.5 text-galvanizing-green bg-[rgba(31,154,58,0.08)] rounded-full font-semibold">
                          Latest
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 text-[12px] text-text-grey">
                        {formatDate(latest.uploadDate || latest.upload_date || latest.created_at || '')}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadReport(latest)}
                          className="flex items-center gap-1.5 border-[#E2E5EA] text-text-grey hover:bg-page-bg h-8 px-3 text-[12px]"
                          disabled={!latest.fileUrl && !latest.file_url}
                        >
                          <Download className="h-3.5 w-3.5" />
                          Download
                        </Button>
                        <ApproveButton
                          docId={latest.docId || latest.doc_id}
                          isApproved={latest.approvalStatus === 'approved' || latest.approval_status === 'approved'}
                          apiType="registry"
                          onStatusChange={() => refresh?.()}
                        />
                      </div>
                    </div>

                    {hasOlderVersions && (
                      <>
                        <button
                          type="button"
                          onClick={() => togglePipeExpansion(pipeId)}
                          className="w-full px-4 py-2 border-t border-[#E2E5EA] flex items-center gap-1.5 text-[12px] text-heritage-grey hover:bg-page-bg transition-colors"
                        >
                          {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          <span>{isExpanded ? 'Hide' : 'Show'} {older.length} older {older.length === 1 ? 'version' : 'versions'}</span>
                        </button>
                        {isExpanded && (
                          <div className="border-t border-[#E2E5EA]">
                            {older.map((doc: any, index: number) => (
                              <div
                                key={doc.docId || doc.doc_id || index}
                                className="flex items-center gap-3 px-4 py-2.5 hover:bg-page-bg transition-colors border-t border-[#EEF0F3] first:border-t-0"
                              >
                                <div className="flex items-center flex-shrink-0 min-w-[110px]">
                                  {doc.docRef && (
                                    <span className="text-[11px] px-2 py-0.5 text-heritage-grey bg-[#EEF0F3] rounded-full font-medium">
                                      v{doc.docRef}
                                    </span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 text-[12px] text-heritage-grey">
                                  {formatDate(doc.uploadDate || doc.upload_date || doc.created_at || '')}
                                </div>
                                <div className="flex-shrink-0">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadReport(doc)}
                                    className="flex items-center gap-1.5 border-[#E2E5EA] text-text-grey hover:bg-white h-8 px-3 text-[12px]"
                                    disabled={!doc.fileUrl && !doc.file_url}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    Download
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

